import Card from '@/components/common/Card'
import FormField from '@/components/common/FormField'
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { Button } from '@/components/ui/button'
import { WfhService, type ICreateWfhPayload } from '@/services/WFH/WfhService'
import { showToastNotification } from '@/utility/helper'
import { RoutesLink } from '@/utility/RouteLinks'
import { errorTypes } from '@/utility/validation'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { ThrowError } from '@/api/callApi'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import FullPageLoader from '@/components/common/FullPageLoader'
import moment from 'moment'

const ADDWFH: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const { siteId } = useCurrentUser();

    const { register, handleSubmit, watch, setValue, clearErrors, trigger, formState: { errors } } = useForm<ICreateWfhPayload>({
        mode: "onChange",
        defaultValues: {
            duration: "FULL_DAY",
            checkInDate: "",
            checkOutDate: "",
            numberOfDays: 1,
            reason: "",
        }
    });

    const getWfhById = async () => {
        if (!id) return;
        setIsFetching(true);
        try {
            const response = await WfhService.getWfhById(id);
            if (response?.status && response.data) {
                const data = response.data;
                setValue("checkInDate", data.checkInDate ? data.checkInDate.split('T')[0] : "");
                setValue("checkOutDate", data.checkOutDate ? data.checkOutDate.split('T')[0] : "");
                setValue("duration", data.duration);
                setValue("numberOfDays", data.numberOfDays);
                setValue("reason", data.reason);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsFetching(false);
        }
    }

    useEffect(() => {
        if (id) {
            getWfhById();
        }
    }, [id]);

    const checkInDate = watch("checkInDate");
    const checkOutDate = watch("checkOutDate");
    const duration = watch("duration");

    // Auto-calculate numberOfDays and sync checkOutDate for half days
    useEffect(() => {
        if (duration === "FIRST_HALF" || duration === "SECOND_HALF") {
            setValue("numberOfDays", 0.5, { shouldValidate: true, shouldDirty: true });
            clearErrors("numberOfDays");
            if (checkInDate) {
                setValue("checkOutDate", checkInDate, { shouldValidate: true, shouldDirty: true });
            }
        } else if (checkInDate && checkOutDate) {
            const start = new Date(checkInDate);
            const end = new Date(checkOutDate);
            const diffTime = end.getTime() - start.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            setValue("numberOfDays", diffDays > 0 ? diffDays : 1, { shouldValidate: true, shouldDirty: true });
            clearErrors("numberOfDays");
        }
    }, [checkInDate, checkOutDate, duration, setValue, clearErrors]);

    // Ensure checkOutDate is not prior to checkInDate and revalidate checkOutDate
    useEffect(() => {
        if (checkInDate && checkOutDate) {
            if (new Date(checkOutDate) < new Date(checkInDate)) {
                setValue("checkOutDate", checkInDate, { shouldValidate: true, shouldDirty: true });
            } else {
                trigger("checkOutDate");
            }
        }
    }, [checkInDate, checkOutDate, setValue, trigger]);

    const onSubmit = async (data: ICreateWfhPayload) => {
        try {
            setIsLoading(true);
            const payload: ICreateWfhPayload = {
                checkInDate: data.checkInDate || watch("checkInDate"),
                checkOutDate: data.checkOutDate || watch("checkOutDate"),
                duration: data.duration,
                numberOfDays: Number(data.numberOfDays) || watch("numberOfDays"),
                reason: data.reason,
                siteId: siteId,
            }

            let response;
            if (id) {
                response = await WfhService.editWfh(id, payload);
            } else {
                response = await WfhService.createWfh(payload);
            }

            if (response?.status) {
                showToastNotification("success", response?.message || (id ? "WFH request updated successfully" : "WFH applied successfully"))
                navigate(RoutesLink.wfhRequest)
            } else {
                showToastNotification("danger", response?.message || "Failed to submit WFH request")
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    }

    if (isFetching) {
        return (
            <Layout>
                <FullPageLoader />
            </Layout>
        )
    }

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading
                    title={id ? "Edit WFH Request" : "Apply for Work From Home"}
                    isBackButtonVisible
                    onClick={() => navigate(-1)}
                />
                <Card className='overflow-visible'>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                {...register("duration", { ...errorTypes.required('Duration') })}
                                type="select"
                                // required
                                label="Duration"
                                value={watch("duration") ?? ""}
                                options={[
                                    { value: '', label: 'Select Duration' },
                                    { value: 'FULL_DAY', label: 'Full Day' },
                                    { value: 'FIRST_HALF', label: 'First Half' },
                                    { value: 'SECOND_HALF', label: 'Second Half' },
                                ]}
                                error={errors.duration?.message as string}
                                required
                            />

                            <FormField
                                {...register("checkInDate", {
                                    ...errorTypes.required('Check-In Date'),
                                    validate: (value: any) => {
                                        if (!value) return true;
                                        const minDate = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
                                        if (value < minDate) {
                                            return `Check-In Date cannot be earlier than last month (${moment(minDate).format("DD-MM-YYYY")})`;
                                        }
                                        const currentYearEnd = `${new Date().getFullYear()}-12-31`;
                                        if (value > currentYearEnd) {
                                            return `Check-In Date cannot exceed ${new Date().getFullYear()} (current year)`;
                                        }
                                        return true;
                                    }
                                })}
                                type="date"
                                label="Check-In Date"
                                min={moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD")}
                                max={`${new Date().getFullYear()}-12-31`}
                                error={errors.checkInDate?.message as string}
                                required
                            />

                            <FormField
                                {...register("checkOutDate", {
                                    ...errorTypes.required('Check-Out Date'),
                                    validate: (value: any) => {
                                        if (!value) return true;
                                        if (checkInDate && value < checkInDate) {
                                            return "Check-Out Date cannot be earlier than Check-In Date";
                                        }
                                        const currentYearEnd = `${new Date().getFullYear()}-12-31`;
                                        if (value > currentYearEnd) {
                                            return `Check-Out Date cannot exceed ${new Date().getFullYear()} (current year)`;
                                        }
                                        return true;
                                    }
                                })}
                                type="date"
                                label="Check-Out Date"
                                min={checkInDate || moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD")}
                                max={`${new Date().getFullYear()}-12-31`}
                                disabled={duration === "FIRST_HALF" || duration === "SECOND_HALF"}
                                error={errors.checkOutDate?.message as string}
                                required
                            />

                            <FormField
                                {...register("numberOfDays", { ...errorTypes.required('Number of Days') })}
                                type="number"
                                step="0.5"
                                label="Number of Days"
                                placeholder="Auto-calculated"
                                error={errors.numberOfDays?.message as string}
                                readOnly
                                required
                            />

                            <FormField
                                {...register("reason", {
                                    ...errorTypes.required('Reason for WFH'),
                                    ...errorTypes.description,
                                })}
                                type="textarea"
                                label="Reason"
                                rows={3}
                                minLength={10}
                                maxLength={500}
                                placeholder="Enter reason for WFH"
                                error={errors.reason?.message as string}
                                required
                            />
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? "Submitting..." : id ? "Update WFH" : "Apply WFH"}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </Layout>
    )
}

export default ADDWFH